import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import AddressAutocomplete from '../components/AddressAutocomplete';

const FEATURES = [
  { title: 'ARV Analysis', description: 'Instantly calculate After Repair Value using real comp data from your market.' },
  { title: 'ROI Calculator', description: 'Get precise return on investment for flips, rentals, and BRRRR strategies.' },
  { title: 'Rehab Estimator', description: 'Estimate rehab costs by room with Low/Mid/High quality options.' },
  { title: 'Deal Score', description: 'Get a 0-100 deal quality score based on equity, rent, and risk factors.' },
  { title: 'PDF Reports', description: 'Export professional deal analysis reports to share with partners or lenders.' },
  { title: 'AI Insights', description: 'GPT-4o powered analysis explains why a deal is good or bad in plain English.' }
];

const STATS = [
  { value: '10,000+', label: 'Deals Analyzed' },
  { value: '98%', label: 'Accuracy Rate' },
  { value: '< 5 sec', label: 'Analysis Speed' },
  { value: '$2.4M', label: 'Profit Generated' }
];

export default function Home() {
  const [address, setAddress] = useState('');
  const [banner, setBanner]   = useState('');
  const navigate              = useNavigate();
  const [searchParams]        = useSearchParams();
  const { user, updateUser }  = useAuth();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const tier      = searchParams.get('tier');
    if (!sessionId) return;

    const token = localStorage.getItem('proparv_token');
    if (!token) return;

    fetch(`/api/stripe/verify-session?session_id=${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setBanner(`Your ${tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : ''} subscription is now active!`);
          if (data.subscription && user) updateUser({ ...user, subscription: data.subscription });
        }
      })
      .catch(() => {});

    window.history.replaceState({}, '', '/');
  }, []);


  const handleSearch = (e) => {
    e.preventDefault();
    if (address.trim()) {
      navigate(`/analyze?address=${encodeURIComponent(address.trim())}`);
    }
  };

  return (
    <div className="page">
      {banner && (
        <div style={{
          position: 'fixed', top: 64, left: 0, right: 0, zIndex: 999,
          background: 'linear-gradient(90deg, #059669, #10b981)',
          color: '#fff', textAlign: 'center', padding: '14px 20px',
          fontWeight: 700, fontSize: '1rem', letterSpacing: '0.01em',
          boxShadow: '0 4px 20px rgba(16,185,129,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10
        }}>
          <span style={{ fontSize: '1.2rem' }}>✓</span>
          {banner}
          <button onClick={() => setBanner('')} style={{
            background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
            fontSize: '1.1rem', marginLeft: 12, opacity: 0.8, lineHeight: 1
          }}>✕</button>
        </div>
      )}
      {/* Hero Section */}
      <section style={{
        minHeight: '88vh',
        display: 'flex',
        alignItems: 'center',
        background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(37,99,235,0.15) 0%, transparent 70%), var(--bg-dark)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background grid pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 1, padding: '60px 20px' }}>
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.4)',
              borderRadius: 999, padding: '6px 16px', marginBottom: 28
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary-light)' }} />
              <span style={{ fontSize: '0.8rem', color: 'var(--primary-light)', fontWeight: 600, letterSpacing: '0.05em' }}>
                POWERED BY GPT-4o + REAL MARKET DATA
              </span>
            </div>

            {/* Headline */}
            <h1 style={{
              fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
              fontWeight: 900,
              lineHeight: 1.1,
              marginBottom: 20,
              letterSpacing: '-0.03em'
            }}>
              Analyze Any Real Estate<br />
              <span style={{
                background: 'linear-gradient(135deg, #3b82f6, #2563eb, #1d4ed8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Deal in Seconds
              </span>
            </h1>

            {/* Subheadline */}
            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.2rem)',
              color: 'var(--text-secondary)',
              maxWidth: 580,
              margin: '0 auto 40px',
              lineHeight: 1.7
            }}>
              Get instant ARV, ROI projections, rehab estimates, and AI-powered deal insights for any property.
              Make smarter investment decisions — before you make an offer.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} style={{
              display: 'flex',
              gap: 0,
              maxWidth: 600,
              margin: '0 auto 48px',
              background: 'var(--bg-card)',
              border: '2px solid var(--border)',
              borderRadius: 14,
              padding: 6,
              boxShadow: '0 0 0 1px rgba(37,99,235,0.1)',
              transition: 'border-color 0.2s, box-shadow 0.2s'
            }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--primary)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={(addr) => navigate(`/analyze?address=${encodeURIComponent(addr)}`)}
                placeholder="Enter property address (e.g. 123 Oak St, Austin, TX)"
              />
              <button
                type="submit"
                className="btn btn-primary"
                style={{ borderRadius: 10, padding: '12px 24px', fontSize: '0.95rem' }}
              >
                Analyze Deal
              </button>
            </form>

            {/* Example addresses */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Try:</span>
              {['123 Main St, Dallas, TX', '456 Oak Ave, Chicago, IL', '789 Pine Rd, Miami, FL'].map(ex => (
                <button
                  key={ex}
                  onClick={() => { setAddress(ex); navigate(`/analyze?address=${encodeURIComponent(ex)}`); }}
                  style={{
                    background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                    padding: '4px 10px', fontSize: '0.78rem', color: 'var(--primary-light)',
                    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s'
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
            {STATS.map((stat, i) => (
              <div key={i} style={{
                padding: '28px 20px',
                textAlign: 'center',
                borderRight: i < STATS.length - 1 ? '1px solid var(--border)' : 'none'
              }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--primary-light)', marginBottom: 4 }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: 800, marginBottom: 12, letterSpacing: '-0.02em' }}>
              Everything You Need to Evaluate Any Deal
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', maxWidth: 480, margin: '0 auto' }}>
              Professional-grade tools that used to cost thousands — now in your browser for free.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {FEATURES.map((feature, i) => (
              <div
                key={i}
                className="card card-hover"
                style={{ cursor: 'default', animation: `fadeIn 0.4s ease ${i * 0.08}s both` }}
              >
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: 'var(--primary)', marginBottom: 16
                }} />
                <h3 style={{ fontSize: '1rem', marginBottom: 8, color: 'var(--text-primary)' }}>{feature.title}</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 0',
        background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(37,99,235,0.12) 0%, transparent 70%)',
        borderTop: '1px solid var(--border)'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 800, marginBottom: 16 }}>
            Start Analyzing Deals Now
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 32, fontSize: '1rem' }}>
            No signup required. Just enter an address and get your analysis.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/analyze')} className="btn btn-primary btn-lg">
              Analyze a Property
            </button>
            <button onClick={() => navigate('/register')} className="btn btn-outline btn-lg">
              Create Free Account
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid var(--border)',
        padding: '24px 0',
        textAlign: 'center'
      }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
          © 2024 PROPARV — Real Estate Deal Analyzer. For informational purposes only.
        </p>
      </footer>
    </div>
  );
}
